import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  type TooltipContentProps,
} from 'recharts'
import {
  BanknotesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Text, LoadingSpinner } from '../../components/ui'
import type { MarketingPaymentListRow } from '../../types/marketingNotas'
import { formatCurrency } from './notasUi'
import { isPaymentOverdue, paymentSupplierName } from './financeiroUi'

type FinanceiroDashboardTabProps = {
  rows: MarketingPaymentListRow[]
  loading?: boolean
  hasSearched?: boolean
}

const STATUS_COLORS = {
  Pago: '#10b981',
  'Pendente no prazo': '#f59e0b',
  Atrasado: '#ef4444',
} as const

const TYPE_COLORS: Record<string, string> = {
  'NF-e': '#0ea5e9',
  'NFS-e': '#8b5cf6',
  Contrato: '#6366f1',
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100
}

function paymentTypeLabel(row: MarketingPaymentListRow): string {
  if (row.invoice?.document_type === 'nfse') return 'NFS-e'
  if (row.invoice?.document_type === 'nfe') return 'NF-e'
  if (row.contract_id || row.contract) return 'Contrato'
  return 'Outros'
}

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  color: string
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color }) => (
  <div className="flex items-center gap-4 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <Text size="xs" color="muted" className="font-medium uppercase tracking-wider">
        {label}
      </Text>
      <p className="truncate text-xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  </div>
)

const EmptyChart: React.FC<{ message?: string }> = ({
  message = 'Nenhuma parcela encontrada.',
}) => (
  <div className="flex h-full items-center justify-center">
    <Text size="sm" color="muted">
      {message}
    </Text>
  </div>
)

const currencyTooltip = ({ active, payload, label }: TooltipContentProps) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow dark:border-gray-700 dark:bg-gray-800">
      {label ? (
        <p className="mb-1 font-medium text-gray-900 dark:text-white">{label}</p>
      ) : null}
      {payload.map((item) => (
        <p key={item.name} style={{ color: item.color }} className="text-gray-700 dark:text-gray-200">
          {item.name}: {formatCurrency(Number(item.value || 0))}
        </p>
      ))}
    </div>
  )
}

const FinanceiroDashboardTab: React.FC<FinanceiroDashboardTabProps> = ({
  rows,
  loading = false,
  hasSearched = true,
}) => {
  const emptyMessage = hasSearched
    ? 'Nenhuma parcela encontrada com os filtros aplicados.'
    : 'Ajuste os filtros e clique em Buscar para carregar os registros.'

  const totals = useMemo(() => {
    let pending = 0
    let paid = 0
    let overdue = 0
    for (const row of rows) {
      if (row.amount == null) continue
      const amount = Number(row.amount)
      if (row.payment_status === 'paid') {
        paid += amount
        continue
      }
      pending += amount
      if (isPaymentOverdue(row.payment_status, row.due_date)) overdue += amount
    }
    return {
      pending: roundMoney(pending),
      paid: roundMoney(paid),
      overdue: roundMoney(overdue),
      total: roundMoney(pending + paid),
    }
  }, [rows])

  const byStatusChartData = useMemo(() => {
    let paid = 0
    let pendingOnTime = 0
    let overdue = 0
    for (const row of rows) {
      if (row.amount == null) continue
      const amount = Number(row.amount)
      if (row.payment_status === 'paid') {
        paid += amount
        continue
      }
      if (isPaymentOverdue(row.payment_status, row.due_date)) overdue += amount
      else pendingOnTime += amount
    }
    return (
      [
        { name: 'Pago', value: roundMoney(paid), color: STATUS_COLORS.Pago },
        {
          name: 'Pendente no prazo',
          value: roundMoney(pendingOnTime),
          color: STATUS_COLORS['Pendente no prazo'],
        },
        { name: 'Atrasado', value: roundMoney(overdue), color: STATUS_COLORS.Atrasado },
      ] as const
    ).filter((row) => row.value > 0)
  }, [rows])

  const byMonthChartData = useMemo(() => {
    if (rows.length === 0) return []
    const monthMap = new Map<string, { key: string; name: string; paid: number; pending: number }>()
    for (const row of rows) {
      const key = (row.due_date || '').slice(0, 7)
      if (!key || key.length < 7) continue
      let entry = monthMap.get(key)
      if (!entry) {
        entry = { key, name: formatMonthLabel(key), paid: 0, pending: 0 }
        monthMap.set(key, entry)
      }
      if (row.amount == null) continue
      const amount = Number(row.amount)
      if (row.payment_status === 'paid') entry.paid += amount
      else entry.pending += amount
    }
    return Array.from(monthMap.values())
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((row) => ({
        name: row.name,
        paid: roundMoney(row.paid),
        pending: roundMoney(row.pending),
      }))
  }, [rows])

  const bySupplierChartData = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of rows) {
      if (row.payment_status === 'paid' || row.amount == null) continue
      const name = paymentSupplierName(row) || 'Sem fornecedor'
      map.set(name, (map.get(name) || 0) + Number(row.amount))
    }
    return Array.from(map.entries())
      .map(([name, pending]) => ({ name, pending: roundMoney(pending) }))
      .sort((a, b) => b.pending - a.pending)
      .slice(0, 8)
  }, [rows])

  const byTypeChartData = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of rows) {
      if (row.amount == null) continue
      const name = paymentTypeLabel(row)
      map.set(name, (map.get(name) || 0) + Number(row.amount))
    }
    return Array.from(map.entries())
      .map(([name, value]) => ({
        name,
        value: roundMoney(value),
        color: TYPE_COLORS[name] || '#6b7280',
      }))
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value)
  }, [rows])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (!hasSearched) {
    return (
      <div className="rounded-lg bg-white p-10 text-center shadow dark:bg-gray-800">
        <Text size="sm" color="muted">
          {emptyMessage}
        </Text>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Pendente"
          value={formatCurrency(totals.pending)}
          icon={<BanknotesIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          color="bg-amber-50 dark:bg-amber-900/30"
        />
        <KpiCard
          label="Pago"
          value={formatCurrency(totals.paid)}
          icon={<CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />}
          color="bg-emerald-50 dark:bg-emerald-900/30"
        />
        <KpiCard
          label="Atrasado"
          value={formatCurrency(totals.overdue)}
          icon={
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          }
          color="bg-red-50 dark:bg-red-900/30"
        />
        <KpiCard
          label="Total filtrado"
          value={formatCurrency(totals.total)}
          icon={<ChartBarIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          color="bg-primary-50 dark:bg-primary-900/30"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Valor por situação
            </Text>
            <Text size="sm" color="muted">
              Pago / pendente / atrasado
            </Text>
          </div>
          <div className="h-64">
            {byStatusChartData.length === 0 ? (
              <EmptyChart message={emptyMessage} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byStatusChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) =>
                      `${name} ${Math.round((percent || 0) * 100)}%`
                    }
                  >
                    {byStatusChartData.map((row) => (
                      <Cell key={row.name} fill={row.color} />
                    ))}
                  </Pie>
                  <Tooltip content={currencyTooltip} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Valor por tipo
            </Text>
            <Text size="sm" color="muted">
              NF-e / NFS-e / Contrato
            </Text>
          </div>
          <div className="h-64">
            {byTypeChartData.length === 0 ? (
              <EmptyChart message={emptyMessage} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byTypeChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) =>
                      `${name} ${Math.round((percent || 0) * 100)}%`
                    }
                  >
                    {byTypeChartData.map((row) => (
                      <Cell key={row.name} fill={row.color} />
                    ))}
                  </Pie>
                  <Tooltip content={currencyTooltip} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Vencimentos por mês
            </Text>
            <Text size="sm" color="muted">
              Pago e pendente
            </Text>
          </div>
          <div className="h-64">
            {byMonthChartData.length === 0 ? (
              <EmptyChart message={emptyMessage} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonthChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-gray-200 dark:stroke-gray-700"
                  />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={currencyTooltip} />
                  <Legend />
                  <Bar dataKey="paid" name="Pago" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                  <Bar
                    dataKey="pending"
                    name="Pendente"
                    stackId="a"
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Fornecedores com pendência
            </Text>
            <Text size="sm" color="muted">
              Top 8
            </Text>
          </div>
          <div className="h-64">
            {bySupplierChartData.length === 0 ? (
              <EmptyChart message="Nenhuma pendência nos filtros aplicados." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bySupplierChartData} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-gray-200 dark:stroke-gray-700"
                  />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip content={currencyTooltip} />
                  <Bar
                    dataKey="pending"
                    name="Pendente"
                    fill="#ef4444"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinanceiroDashboardTab
